/**
 * NewGameWizard — Create, from the creator's idea to a working game, as one
 * page and one film. The flow model is PURE (./flow.ts); this shell renders the
 * concept form, then follows the server's journey without asking anything:
 *
 *  - CONCEPT: free-text concept + optional title, links and players. Create
 *    saves the draft (`createCreation`) and fires `analyzeConcept`.
 *  - THE FILM: analysis, its questions (offered inside the film), the composed
 *    plan, its steps, the proof and the first build all play in ONE
 *    CreationShow, so the film never restarts. The server runs every stage from
 *    its queue; this page only reads saved progress.
 *  - THE END: the film's finale with Play, or the last-resort screen
 *    (`CreateLastResort`): an outage waits, everything else keeps the idea and
 *    offers Start again.
 */
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ConceptAnalysisDraft, ComposedCreateRunV1, Creation, CreationVersion, PlatformSettings } from '@djgg/creations';
import { composedFirstBuildNextV1, composedNotInYetLineV1, composedPlannedStepsV1, creationFilmRunIdentityV1 } from '@djgg/creations';
import { useAuth, useServices } from '../../services/context.js';
import { useSubscription } from '../../hooks/useSubscription.js';
import { useNow } from '../../hooks/useNow.js';
import { playUrl } from '../../services/playUrl.js';
import { readIdeaDraft, saveIdeaDraft } from './ideaDraft.js';
import { CONCEPT_EXAMPLE } from './conceptExample.js';
import { wizardSlug, CONCEPT_MAX_LEN, MAX_REFERENCE_URLS } from '../wizard.js';
import {
  EMPTY_FLOW,
  ANALYSIS_STATE_TITLE,
  FIRST_BUILD_REPAIR_TITLE,
  canResolveClarifications,
  isAnalysisLive,
  stepErrors,
  proposedGameTitle,
  wizardReducer,
  type WizardFlowState,
} from './flow.js';
import { DecisionControl } from './DecisionControl.js';
import { ComposedCreateProgress, type ComposedStageView } from './ComposedCreateProgress.js';
import { CreateLastResort, createLastResortV1 } from './CreateLastResort.js';
import { ANALYSIS_UNCONFIRMED_MESSAGE, OFFLINE_MESSAGE, plainThrownError } from './creatorCopy.js';
import { COMPOSED_STAGE_LABELS, composedCreateStepLabelV1, composedPlanTemplateTitleV1 } from './composedCreate.js';
import { newBuildCommandId } from '../buildCommands.js';
import { readDraftRequest, saveDraftRequest, clearDraftRequest, clearResumedDraftRequest, type PendingDraftRequest } from './draftRequest.js';
import { CREATION_GUIDES } from '../../components/CreationGuide.js';
import { engineOptions, engineForId, defaultBuildModelId } from '../engineOptions.js';
import { journeyStallWatch } from '../journeyStall.js';
import { reduceBuildStatus } from '../buildStatus.js';
import { WizardStoryReel } from './WizardStoryReel.js';
import { useCreationFilm } from '../useCreationFilm.js';
import { markCreateWaiting } from '../createWaiting.js';

/** Callable failures that do not prove the server operation failed. Firebase
 * can surface these after the request has already committed and continued. */
function isAmbiguousAnalysisError(error: unknown): boolean {
  const raw = (error as { code?: unknown } | null)?.code;
  if (typeof raw !== 'string') return false;
  const code = raw.replace(/^functions\//, '');
  return ['cancelled', 'deadline-exceeded', 'internal', 'unavailable', 'unknown'].includes(code);
}

function initialFlow(resume: (Creation & { id: string }) | null | undefined): WizardFlowState {
  if (!resume) {
    return { ...EMPTY_FLOW, engine: engineForId(defaultBuildModelId()) ?? null };
  }
  return wizardReducer(
    { ...EMPTY_FLOW },
    {
      type: 'hydrate',
      title: resume.title,
      engine: resume.engine,
      concept: resume.concept ?? '',
      answers: resume.settings ?? {},
      creationId: resume.id,
      gameDesign: resume.gameDesign ?? null,
      referenceBriefs: resume.referenceBriefs ?? [],
      analysisDraft: resume.analysisDraft,
      settingsRev: resume.settingsRev,
    },
  );
}

/** The saved composed run as the film's progress lines (superadmin details). */
function composedStages(durable: ComposedCreateRunV1): ComposedStageView[] {
  const plan = durable.plan ?? null;
  const planned = plan ? composedPlannedStepsV1(plan, null) : [];
  const stageStatus = (id: string, done: boolean): ComposedStageView['status'] => {
    if (done) return 'done';
    if (durable.stage !== id) return 'pending';
    if (durable.status === 'failed') return 'error';
    return durable.status === 'claimed' ? 'running' : 'pending';
  };
  return [
    { id: 'plan', label: COMPOSED_STAGE_LABELS.plan, status: stageStatus('plan', Boolean(plan)) },
    ...planned.map((step, index) => ({ id: `step-${index}`, label: composedCreateStepLabelV1(step),
      status: stageStatus(`step-${index}`, durable.steps.length > index) })),
    { id: 'synthesis', label: COMPOSED_STAGE_LABELS.synthesis, status: stageStatus('synthesis', Boolean(durable.result)) },
  ];
}

export function NewGameWizard({
  onAnalysisSaved,
  resume,
  creationFilmsEnabled = true,
}: {
  /** Called only after analysis returns durable state, never while claiming it. */
  onAnalysisSaved?: (creationId: string) => void;
  /** The saved draft this page follows, kept live by its route; null/absent = fresh wizard. */
  resume?: (Creation & { id: string }) | null;
  /** Historical callers may still send this retired rollout hint. */
  creationExperience?: PlatformSettings['creationExperience'];
  creationFilmsEnabled?: boolean;
}) {
  const services = useServices();
  const navigate = useNavigate();
  const { user } = useAuth();
  const pendingDraft = useRef<PendingDraftRequest | null>(resume ? null : readDraftRequest(user?.uid ?? ''));
  useEffect(() => {
    // The durable route already carries this creation's identity, including
    // when its analysis was ready before the wizard mounted.
    if (resume) void clearResumedDraftRequest(user?.uid ?? '', resume.id);
  }, [resume?.id, user?.uid]);
  const options = engineOptions();
  const [state, dispatch] = useReducer(wizardReducer, resume ?? null, (draft) => {
    const initial = initialFlow(draft);
    if (draft) return initial;
    let recovered = { ...initial, concept: '' };
    if (pendingDraft.current) {
      const { request, refUrls } = pendingDraft.current;
      recovered = { ...initial, title: request.title, concept: request.concept ?? '', refUrls,
        engine: request.engine, creatorAnswers: request.settings };
    }
    try {
      const idea = readIdeaDraft(window.sessionStorage, user?.uid ?? '');
      return idea ? { ...recovered, ...idea, creatorAnswers: idea.answers ?? {} } : recovered;
    } catch { return recovered; }
  });
  const [ideaSaved, setIdeaSaved] = useState(false);
  const [discardRequested, setDiscardRequested] = useState(false);
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine !== false);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  const submitInFlight = useRef(false);
  const conceptRef = useRef<HTMLTextAreaElement>(null);
  const [serverError, setServerError] = useState('');
  // `analysisDraft` is deliberately omitted from browser creation rows. An
  // undefined value therefore needs the guarded callable; null means it was
  // fetched and no durable analysis state exists.
  const [resumeDraft, setResumeDraft] = useState<ConceptAnalysisDraft | null | undefined>(resume?.analysisDraft);
  const [resumeLoading, setResumeLoading] = useState(Boolean(resume && resume.analysisDraft === undefined));
  const [resumeAttempt, setResumeAttempt] = useState(0);
  const [analysisSettledTick, setAnalysisSettledTick] = useState(0);
  const stateRef = useRef(state);
  stateRef.current = state;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // The composed run is the server's: its queue runs every stage and accepts the
  // first build. This page reads the saved run and, only when the queue looks
  // stalled, asks the server to look at it again (never to repeat a failed stage).
  const composedAdvance = useRef({ inFlight: false });
  const composedStall = useRef(journeyStallWatch());
  const durableRun = resumeDraft?.composedRun
    && resumeDraft.composedRun.sourceOperationId === state.analysisOperationId
    && resumeDraft.composedRun.sourceRevision === state.analysisRevision
    ? resumeDraft.composedRun : null;
  async function wakeComposedCreate(): Promise<void> {
    const current = stateRef.current;
    if (composedAdvance.current.inFlight || !current.creationId || !current.design
      || !current.analysisOperationId || current.analysisRevision === null) return;
    composedAdvance.current.inFlight = true;
    try {
      await services.callable.advanceComposedCreate({ creationId: current.creationId,
        analysisOperationId: current.analysisOperationId, analysisRevision: current.analysisRevision, design: current.design });
    } catch { /* The saved run stays the authority; the next read shows what happened. */ }
    finally { composedAdvance.current.inFlight = false; }
  }
  // Only the saved-draft route follows a run: the fresh route hands off to it as
  // soon as analysis is saved.
  const followsRun = Boolean(resume) && !resumeLoading && state.step === 'review' && Boolean(state.creationId)
    && (state.analysisStatus === 'proposal-ready' || state.analysisStatus === 'consumed');
  // A saved proposal with no run at all (its queue task was never written, or it
  // was saved by an older page that paused for a review) is started by the same
  // wake: the server's stage claim makes it the run's one start.
  useEffect(() => {
    if (!followsRun || (resume?.latestVersion ?? 0) > 0 || resumeDraft === undefined) return;
    if (!durableRun) {
      if (state.analysisStatus === 'proposal-ready' && !resumeDraft?.composedRun && composedStall.current('no-run')) void wakeComposedCreate();
      return;
    }
    const stalled = composedStall.current(`${durableRun.status}:${durableRun.stage}:${durableRun.attempt}:${durableRun.steps.length}`);
    if (stalled && (durableRun.status === 'ready' || durableRun.status === 'prepared') && !durableRun.finalizationFailure) void wakeComposedCreate();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- wake only reads current refs
  }, [followsRun, durableRun, resumeDraft, state.analysisStatus, resume?.latestVersion]);

  // A stage keeps running in Functions when mobile Safari suspends or restores
  // this page. Read the saved checkpoint until the first build exists.
  const runSettled = durableRun?.status === 'failed' || (resume?.latestVersion ?? 0) > 0;
  useEffect(() => {
    if (!followsRun || runSettled || !state.creationId) return;
    let cancelled = false;
    let reading = false;
    const refresh = async () => {
      if (reading) return;
      reading = true;
      try {
        const result = await services.callable.getConceptAnalysisResume({ creationId: state.creationId! });
        if (!cancelled && result.analysisDraft) setResumeDraft(result.analysisDraft);
      } catch { /* A read failure changes nothing. */ }
      finally { reading = false; }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 5_000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [services, state.creationId, followsRun, runSettled]);

  // The first build, followed in the same film. The route keeps `resume` live.
  const firstBuild = useSubscription<(CreationVersion & { id: string }) | null>(
    cb => resume && resume.latestVersion > 0 ? services.subscribe.version(resume.id, resume.latestVersion, cb) : () => {},
    null, [resume?.id, resume?.latestVersion]);
  const now = useNow(firstBuild?.buildStatus === 'failed' ? 30_000 : 0);
  const firstBuildNext = resume && firstBuild ? composedFirstBuildNextV1(resume, firstBuild, now) : null;
  const buildView = useMemo(() => firstBuild ? reduceBuildStatus(firstBuild) : null, [firstBuild]);
  const ready = firstBuildNext === 'finished' && (resume?.activeVersion ?? 0) > 0;
  const [cancelling, setCancelling] = useState(false);
  async function cancelFirstBuild(): Promise<void> {
    if (!resume || cancelling) return;
    setCancelling(true);
    try { await services.callable.cancelBuild({ creationId: resume.id, version: resume.latestVersion }); }
    catch (error) { setServerError(plainThrownError(error, 'We couldn’t stop the build. Please try again.')); }
    finally { setCancelling(false); }
  }

  const lastResort = createLastResortV1({
    analysisStatus: state.analysisStatus,
    analysisError: state.analysisError,
    run: durableRun,
    firstBuild: firstBuildNext,
  });
  useEffect(() => {
    if (lastResort?.kind === 'waiting' && state.creationId) markCreateWaiting(window.localStorage, user?.uid ?? '', state.creationId);
  }, [lastResort?.kind, state.creationId, user?.uid]);
  /** The idea goes back into a fresh wizard; the failed draft stays saved for engineers. */
  function startAgain(): void {
    try {
      saveIdeaDraft(window.sessionStorage, user?.uid ?? '', {
        title: state.title, concept: state.concept, refUrls: [...state.refUrls],
        ...(Object.keys(state.creatorAnswers).length ? { answers: state.creatorAnswers } : {}),
      });
    } catch { /* The idea is also on the saved draft. */ }
    navigate('/studio/create');
  }

  useEffect(() => {
    if (resume) return;
    try {
      const stored = saveIdeaDraft(window.sessionStorage, user?.uid ?? '', state.creationId && !pendingDraft.current ? null : {
        title: state.title, concept: state.concept, refUrls: [...state.refUrls],
        ...(Object.keys(state.creatorAnswers).length ? { answers: state.creatorAnswers } : {}),
      });
      setIdeaSaved(stored && !state.creationId);
    } catch { setIdeaSaved(false); }
  }, [resume, user?.uid, state.title, state.concept, state.refUrls, state.creationId, state.creatorAnswers]);

  const { run: filmRun, materials: filmMaterials } = useCreationFilm(state.creationId, creationFilmsEnabled, {
    kind: 'analysis',
    runId: state.creationId && state.analysisOperationId && state.analysisRevision !== null
      ? creationFilmRunIdentityV1(state.creationId, { kind: 'analysis', operationId: state.analysisOperationId, revision: state.analysisRevision }).runId
      : null,
  });
  const errors = stepErrors(state);
  const showError = (key: string) => (state.submitAttempted ? errors[key] : undefined);
  const proposedTitle = proposedGameTitle(state.concept);
  const effectiveTitle = state.title.trim() || proposedTitle;
  const slug = wizardSlug(effectiveTitle);

  // The PLATFORM default engine is the DB setting (superadmin-editable, e.g.
  // flipped provider-wide during an outage) — the registry seed is only the
  // pre-read placeholder. Resolve once on a fresh wizard; never stomp a
  // picker the creator already changed.
  const engineRef = useRef(state.engine);
  engineRef.current = state.engine;
  useEffect(() => {
    let cancelled = false;
    void services.repos.settings
      .get()
      .then((s) => {
        const untouched = engineRef.current?.model === defaultBuildModelId();
        if (!cancelled && !resume && !pendingDraft.current && untouched && s.defaultEngine) {
          dispatch({ type: 'setEngine', value: { ...s.defaultEngine } });
        }
      })
      .catch(() => {}); // unreadable settings ⇒ the seed default stands
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only seed
  }, []);

  /** Land the saved draft's durable analysis state on this page. */
  function hydrateResume(draft: Creation & { id: string }, analysisDraft: ConceptAnalysisDraft | null): void {
    setResumeDraft(analysisDraft);
    dispatch({
      type: 'hydrate',
      title: draft.title,
      engine: draft.engine,
      concept: draft.concept ?? '',
      answers: draft.settings ?? {},
      creationId: draft.id,
      gameDesign: draft.gameDesign ?? null,
      referenceBriefs: draft.referenceBriefs ?? [],
      analysisDraft: analysisDraft ?? undefined,
      settingsRev: draft.settingsRev,
    });
  }

  // A normal browser row never carries this private field. Hydrate it through
  // the member/admin callable before allowing a resumed concept to submit, so
  // a saved proposal or clarification cannot be replaced by a fresh analysis.
  useEffect(() => {
    if (!resume || resume.analysisDraft !== undefined) return;
    let cancelled = false;
    void services.callable
      .getConceptAnalysisResume({ creationId: resume.id })
      .then(({ analysisDraft }) => {
        if (cancelled) return;
        hydrateResume(resume, analysisDraft);
        setResumeLoading(false);
      })
      .catch((err) => {
        if (!cancelled) setServerError(plainThrownError(err, 'We couldn’t load your saved game.'));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resume identity is fixed; explicit retry only
  }, [resumeAttempt]);

  // The draft row is saved before analysis claims it, so a page opened in that
  // gap reads no analysis at all. Keep watching while the form is showing: a
  // run started by another tab takes this page to the film, and the form's
  // button cannot start a second one with edited inputs.
  const watchForRun = Boolean(resume) && !resumeLoading && !busy && state.step === 'concept' && state.analysisStatus === null;
  useEffect(() => {
    if (!resume || !watchForRun) return;
    let cancelled = false;
    let reading = false;
    const refresh = async () => {
      if (reading || document.hidden) return;
      reading = true;
      try {
        const { analysisDraft } = await services.callable.getConceptAnalysisResume({ creationId: resume.id });
        if (!cancelled && analysisDraft) hydrateResume(resume, analysisDraft);
      } catch { /* The next read tries again; a failed read changes nothing. */ }
      finally { reading = false; }
    };
    const timer = window.setInterval(() => void refresh(), 5_000);
    return () => { cancelled = true; window.clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrateResume only reads dispatch and setters
  }, [services, watchForRun, resume?.title, resume?.concept]);

  const savedSettings = useRef(JSON.stringify(resume?.settings ?? {}));
  const analysisInFlight = useRef(new Set<string>());
  const completedAnalysis = useRef(new Set<string>());
  const recoveryAttempts = useRef(new Set<string>());
  const uncertainAnalysis = useRef(new Map<string, { message: string; nullReads: number }>());

  /** The saved analysis takes the fresh route to the saved-draft route. */
  function finishAnalysis(creationId: string): void {
    if (!mounted.current || completedAnalysis.current.has(creationId)) return;
    completedAnalysis.current.add(creationId);
    if (pendingDraft.current) clearDraftRequest(user?.uid ?? '', pendingDraft.current.request.requestId);
    else void clearResumedDraftRequest(user?.uid ?? '', creationId);
    pendingDraft.current = null;
    saveIdeaDraft(window.sessionStorage, user?.uid ?? '', null);
    onAnalysisSaved?.(creationId);
  }

  function reconcileAnalysis(creationId: string, draft: ConceptAnalysisDraft | null): void {
    setResumeDraft(draft);
    if (!draft) return;
    uncertainAnalysis.current.delete(creationId);
    dispatch({
      type: 'analysisReconciled',
      draft,
      owned: analysisInFlight.current.size > 0,
    });
  }

  function noteMissingAnalysis(creationId: string): void {
    const uncertain = uncertainAnalysis.current.get(creationId);
    if (!uncertain) return;
    const nullReads = uncertain.nullReads + 1;
    if (nullReads < 3) {
      uncertainAnalysis.current.set(creationId, { ...uncertain, nullReads });
      return;
    }
    uncertainAnalysis.current.delete(creationId);
    dispatch({
      type: 'analysisFailed',
      message: ANALYSIS_UNCONFIRMED_MESSAGE,
    });
  }

  function runAnalysis(creationId: string, urls: string[], answers?: Record<string, string>): boolean {
    return trackAnalysis(creationId, `${creationId}:${JSON.stringify(answers ?? {})}`,
      () => services.callable.analyzeConcept({ creationId, urls, ...(answers ? { answers } : {}) }));
  }

  /** "Keep making my game": the server answers the questions on screen now. */
  function skipQuestions(creationId: string, operationId: string, revision: number): void {
    dispatch({ type: 'skipClarification' });
    trackAnalysis(creationId, `${creationId}:skip:${operationId}:${revision}`,
      () => services.callable.skipConceptQuestions({ creationId, operationId, revision }));
  }

  function trackAnalysis(creationId: string, requestKey: string,
    request: () => ReturnType<typeof services.callable.analyzeConcept>): boolean {
    // Offline: the saved claim and the poll below pick this up again on reconnect.
    if (navigator.onLine === false) return false;
    if (analysisInFlight.current.has(requestKey)) return false;
    uncertainAnalysis.current.delete(creationId);
    analysisInFlight.current.add(requestKey);
    void request()
      .then((r) => {
        dispatch({
          type: 'analysisSucceeded',
          ...(r.proposal ? { proposal: r.proposal } : {}),
          analysis: r.analysis,
          operationId: r.operationId,
          revision: r.revision,
          referenceBriefs: r.referenceBriefs,
        });
      })
      .catch(async (err) => {
        // A callable timeout/internal transport error is ambiguous: the
        // server may still finish and persist the proposal. Reconcile once
        // immediately, then let the guarded poll follow a live claim.
        let confirmedMissing = false;
        try {
          const result = await services.callable.getConceptAnalysisResume({ creationId });
          if (result.analysisDraft) {
            reconcileAnalysis(creationId, result.analysisDraft);
            return;
          }
          confirmedMissing = true;
        } catch {
          // For an ambiguous primary error, a failed read is still no evidence
          // that the durable operation failed. The pending poll will retry.
        }
        if (isAmbiguousAnalysisError(err)) {
          uncertainAnalysis.current.set(creationId, {
            message: err instanceof Error ? err.message : 'Analysis request could not be confirmed.',
            nullReads: confirmedMissing ? 1 : 0,
          });
          return;
        }
        dispatch({
          type: 'analysisFailed',
          message: err instanceof Error ? err.message : 'Analysis failed.',
        });
      })
      .finally(() => {
        analysisInFlight.current.delete(requestKey);
        if (mounted.current) setAnalysisSettledTick((tick) => tick + 1);
      });
    return true;
  }

  // Only a reducer-accepted durable success completes the fresh route. This
  // keeps a late stale/failed snapshot from navigating after a newer result.
  useEffect(() => {
    if (
      state.creationId &&
      ['proposal-ready', 'clarification-ready', 'consumed'].includes(state.analysisStatus ?? '')
    ) finishAnalysis(state.creationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- completion is idempotent and keyed by creation
  }, [state.analysisStatus, state.creationId]);

  // The guarded resume projection is safe for members and survives refresh.
  // While the analysis chapter is pending, this durable record is the
  // authority that advances the reducer. Reload/focus never invokes the
  // analyzer again for an operation another request is already running.
  // Questions on screen are read too: when the wait runs out the server answers
  // them, and the page follows that newer revision. A read of the same revision
  // changes nothing, so picks the creator is making are kept.
  useEffect(() => {
    if (
      state.step !== 'analysis' ||
      !state.creationId ||
      !(isAnalysisLive(state.analysisStatus) || state.analysisStatus === 'clarification-ready')
    ) return;
    let cancelled = false;
    let inFlight = false;
    const refresh = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const result = await services.callable.getConceptAnalysisResume({ creationId: state.creationId! });
        if (!cancelled) {
          if (result.analysisDraft) reconcileAnalysis(state.creationId!, result.analysisDraft);
          else noteMissingAnalysis(state.creationId!);
        }
      } catch { /* Keep pending; a read failure is not a durable analysis failure. */ }
      finally { inFlight = false; }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 5_000);
    return () => { cancelled = true; window.clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- state gates restart the single poll; helpers use current refs
  }, [services, state.analysisStatus, state.creationId, state.step]);

  // A claimed operation with an expired lease has no live request left to
  // finish it. Re-enter the analyzer once per durable attempt so its CAS can
  // reclaim the lease; ordinary reloads with a live lease remain read-only.
  useEffect(() => {
    const draft = resumeDraft;
    if (
      state.step !== 'analysis' ||
      state.analysisStatus !== 'recovering' ||
      !state.creationId ||
      !draft ||
      draft.status !== 'claimed' ||
      draft.leaseExpiresAt === null ||
      draft.leaseExpiresAt > Date.now()
    ) return;
    const recoveryKey = `${draft.operationId}:${draft.attempt}`;
    if (recoveryAttempts.current.has(recoveryKey)) return;
    if (runAnalysis(state.creationId, draft.inputs.referenceUrls, draft.partialAnswers)) {
      recoveryAttempts.current.add(recoveryKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one reclaim per durable operation attempt
  }, [analysisSettledTick, resumeDraft, state.analysisStatus, state.creationId, state.step]);

  const refUrls = () => state.refUrls.map((u) => u.trim()).filter((u) => u !== '');

  /** Concept step submit: create (or re-stamp) the draft, then analyze. */
  async function startAnalysis() {
    setServerError('');
    if (navigator.onLine === false) { setServerError(OFFLINE_MESSAGE); return; }
    if (Object.keys(errors).length > 0) {
      dispatch({ type: 'showErrors' });
      return;
    }
    setBusy(true);
    try {
      const title = effectiveTitle;
      const concept = state.concept.trim();
      if (!state.title.trim()) dispatch({ type: 'setTitle', value: title });
      let creationId = state.creationId;
      let expectedSettingsRev = state.expectedSettingsRev;
      if (!creationId || pendingDraft.current) {
        const pending = pendingDraft.current ?? { request: {
          requestId: newBuildCommandId(),
          title,
          slug,
          description: '',
          engine: state.engine!,
          template: 'neutral',
          style: 'action',
          settings: state.creatorAnswers,
          concept,
        }, refUrls: [...state.refUrls] };
        pendingDraft.current = pending;
        saveDraftRequest(user?.uid ?? '', pending);
        const created = await services.callable.createCreation(pending.request);
        creationId = created.creationId;
        expectedSettingsRev = created.creation?.settingsRev ?? created.settingsRev ?? 1;
        const currentSettings = created.creation?.settings;
        const intendedAlreadySaved = currentSettings !== undefined &&
          Object.entries(state.creatorAnswers).every(([key, value]) =>
            JSON.stringify(currentSettings[key as keyof typeof currentSettings]) === JSON.stringify(value));
        // A receipt can recover our own lost response, but must not turn a
        // concurrent editor's revision into permission to overwrite their work.
        if (pending.acceptedSettingsRev !== undefined && pending.acceptedSettingsRev !== expectedSettingsRev && !intendedAlreadySaved) {
          throw new Error('Game settings changed in another session. Open the saved draft to check the current choices before continuing. Your local idea is still saved.');
        }
        pending.acceptedCreationId = creationId;
        pending.acceptedSettingsRev ??= expectedSettingsRev;
        saveDraftRequest(user?.uid ?? '', pending);
        savedSettings.current = JSON.stringify(intendedAlreadySaved ? state.creatorAnswers : pending.request.settings);
        dispatch({ type: 'created', creationId });
        dispatch({ type: 'authoritySaved', settingsRev: expectedSettingsRev });
        // Recover the accepted request first, then apply any newer local edits
        // to that SAME draft. An uncertain response must not mint another one.
        if (title !== pending.request.title || concept !== pending.request.concept) {
          await services.callable.updateCreation({ creationId, title, concept });
        }
      } else if (resume && (title !== resume.title || concept !== (resume.concept ?? ''))) {
        // Resumed draft with edits: analyzeConcept reads the STORED concept,
        // so re-stamp before analyzing.
        await services.callable.updateCreation({
          creationId,
          ...(title !== resume.title ? { title } : {}),
          ...(concept !== (resume.concept ?? '') ? { concept } : {}),
        });
      }
      if (savedSettings.current !== JSON.stringify(state.creatorAnswers)) {
        const updated = await services.callable.updateGameSettings({ creationId,
          settings: state.creatorAnswers, expectedRevision: expectedSettingsRev });
        savedSettings.current = JSON.stringify(state.creatorAnswers);
        dispatch({ type: 'authoritySaved', settingsRev: updated.settingsRev });
      }
      dispatch({ type: 'beginAnalysis' });
      runAnalysis(creationId, refUrls());
    } catch (err) {
      // Only explicit pre-commit rejection allows a new request. A timeout or
      // post-commit error keeps the exact request so a title edit cannot duplicate it.
      if ((err as { details?: { draftRequestRejected?: boolean } })?.details?.draftRequestRejected === true && pendingDraft.current) {
        clearDraftRequest(user?.uid ?? '', pendingDraft.current.request.requestId);
        pendingDraft.current = null;
      }
      setServerError(plainThrownError(err, 'We couldn’t start your game. Your idea is saved in this tab, so you can try again.', { inputRejection: true }));
    } finally {
      setBusy(false);
    }
  }

  const clarifying = state.analysisStatus === 'clarification-ready' && !lastResort;
  const questions = clarifying ? <fieldset data-testid="analysis-clarifications">
    <legend>{ANALYSIS_STATE_TITLE['clarification-ready']}</legend>
    {state.clarificationQuestions.map((question) => (
      <fieldset key={question.id} style={{ margin: '12px 0' }}>
        <legend>{question.prompt}</legend>
        {question.answerMode === 'text' ? (
          <textarea className="textarea" aria-label={question.prompt} maxLength={200}
            value={state.clarificationAnswers[question.id] ?? ''}
            onChange={(event) => dispatch({ type: 'answerClarification', questionId: question.id, value: event.target.value })} />
        ) : question.options.map((option) => (
          <label key={option.id} className="chip" style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', marginRight: 8 }}>
            <input type="radio" name={question.id} value={option.id} checked={state.clarificationAnswers[question.id] === option.id}
              onChange={() => dispatch({ type: 'answerClarification', questionId: question.id, value: option.id })} />
            {option.label}
          </label>
        ))}
      </fieldset>
    ))}
    <button type="button" className="btn btn--primary" style={{ minHeight: 44 }} disabled={!online || !canResolveClarifications(state)}
      onClick={() => { dispatch({ type: 'resolveClarification' }); runAnalysis(state.creationId!, refUrls(), { ...state.clarificationAnswers }); }}>
      Use my answers
    </button>
  </fieldset> : undefined;

  const runningStage = durableRun ? composedStages(durableRun).find(stage => stage.status === 'running') : undefined;
  const activity = ready ? 'Your game is ready'
    // A failed first build is repaired automatically: no failure scene meanwhile.
    : firstBuildNext === 'repair' ? FIRST_BUILD_REPAIR_TITLE
    : buildView ? CREATION_GUIDES[buildView.phase].title
      : runningStage?.label ?? (durableRun ? 'Preparing the next part of your game'
        : state.analysisStatus ? ANALYSIS_STATE_TITLE[state.analysisStatus] : undefined);
  const notInYet = firstBuild?.creationRun?.notInYet;

  return (
    <div className="creation-journey creation-journey--show creation-journey--deej">
      <form
        className="panel creation-journey__form creation-journey__form--deej"
        data-testid="new-game-wizard"
        onSubmit={(e) => {
          e.preventDefault();
          if (submitInFlight.current || busy || resumeLoading || state.step !== 'concept') return;
          submitInFlight.current = true;
          void startAnalysis().finally(() => { submitInFlight.current = false; });
        }}
      >
        {state.step === 'concept' && <h2 className="panel__title">What would you like to play?</h2>}
        {!online && <p className="field__warning" role="status">{OFFLINE_MESSAGE}</p>}
        {!resume && !state.creationId && Boolean(state.title.trim() || state.concept.trim()) && (
          <div className="creation-idea-draft">
          <p className="muted" role="status">
            {ideaSaved ? 'Draft saved in this tab.' : 'Draft saving is unavailable in this browser.'}
          </p>
          {discardRequested ? (
            <div className="field__warning">
              <p>Discard the title, idea and inspiration links in this tab? No saved games will be deleted.</p>
              <button type="button" className="btn" onClick={() => {
                if (pendingDraft.current) clearDraftRequest(user?.uid ?? '', pendingDraft.current.request.requestId);
                pendingDraft.current = null;
                dispatch({ type: 'discardIdea' });
                setDiscardRequested(false);
              }}>Discard this idea</button>
              <button type="button" className="btn" onClick={() => setDiscardRequested(false)}>Keep editing</button>
            </div>
          ) : (
            <button type="button" className="btn btn--sm" disabled={busy} onClick={() => setDiscardRequested(true)}>Start with a new idea</button>
          )}
          </div>
        )}

        {/* Non-visual state for tools/qa/create-wizard-dom.mjs: a creator has no Details drawer. */}
        <div hidden data-testid="create-state" data-step={state.step} data-analysis-status={state.analysisStatus ?? undefined}
          data-stages={durableRun ? JSON.stringify(composedStages(durableRun)) : undefined}
          data-build-requested={(resume?.latestVersion ?? 0) > 0 ? 'true' : 'false'} />
        {lastResort && <CreateLastResort kind={lastResort.kind} message={lastResort.message} onStartAgain={startAgain} />}
        {state.step !== 'concept' && !lastResort && <WizardStoryReel
          filmEnabled={creationFilmsEnabled} filmRun={filmRun} filmMaterials={filmMaterials}
          identity={state.creationId ?? undefined} title={state.title}
          chapter={ready ? 'Ready' : buildView ? 'Build' : durableRun ? 'Plan' : 'Discover'}
          activity={activity}
          lastUpdatedAt={buildView?.since ?? durableRun?.updatedAt ?? resumeDraft?.updatedAt}
          scopeChanges={durableRun?.plan?.notIncluded}
          details={durableRun ? <ComposedCreateProgress plan={durableRun.plan ?? null} templateTitle={composedPlanTemplateTitleV1(durableRun.plan ?? null)}
            stages={composedStages(durableRun)} result={durableRun.result ?? null} /> : undefined}
          choice={questions} choiceKey={clarifying ? `${state.analysisOperationId}:${state.analysisRevision}` : undefined}
          onChoiceSkip={clarifying && state.creationId && state.analysisOperationId && state.analysisRevision !== null
            ? () => skipQuestions(state.creationId!, state.analysisOperationId!, state.analysisRevision!) : undefined}
          stage={clarifying ? 'clarification' : 'analysis'}
          running={!ready}
          ready={ready}
          playUrl={ready && state.creationId ? playUrl(state.creationId) : undefined}
          notInYetLine={notInYet?.length ? composedNotInYetLineV1(notInYet) : undefined}
          onCancel={resume?.buildState === 'building' ? () => void cancelFirstBuild() : undefined} cancelling={cancelling}
          concept={state.concept}
          referenceUrls={refUrls()}
          referenceBriefs={state.step === 'review' ? state.referenceBriefs : resumeDraft?.referenceBriefs}
          videoBriefs={resumeDraft?.videoBriefs}
          design={state.design}
          completedArtifacts={resumeDraft?.progress?.completedArtifacts}
        />}
        {ready && state.creationId && <p><Link to={`/studio/g/${state.creationId}`} data-testid="open-in-studio">Open your game in Studio</Link></p>}
        {ready && <p className="muted" data-testid="wizard-unlisted-note" style={{ fontSize: 12 }}>
          Your game starts <strong>unlisted</strong> — not listed in the gallery, but anyone with the link can play.
          Share it by link right away, and publish it to the gallery from your Studio when it’s ready.
        </p>}

        {state.step === 'concept' && (
          <>
            <div className="field">
              <label className="field__label" htmlFor="concept">
                Your concept
              </label>
              <p id="concept-help" className="muted" style={{ fontSize: 12, margin: '2px 0 6px' }}>
                Describe what you want to play. Deej will shape the details and propose a title.
              </p>
              <textarea
                id="concept"
                ref={conceptRef}
                className="textarea"
                rows={5}
                aria-describedby="concept-help concept-count"
                aria-invalid={state.concept.trim().length > CONCEPT_MAX_LEN}
                value={state.concept}
                onChange={(e) => dispatch({ type: 'setConcept', value: e.target.value })}
                placeholder="Two tanks duel in a walled arena. Shells bounce off walls once…"
              />
              <p id="concept-count" className={state.concept.trim().length > CONCEPT_MAX_LEN ? 'field__error' : 'muted'} style={{ fontSize: 12, marginTop: 5 }}>
                {state.concept.trim().length.toLocaleString('en-US')} / {CONCEPT_MAX_LEN.toLocaleString('en-US')} characters
                {state.concept.trim().length > CONCEPT_MAX_LEN && ' — shorten before analyzing. Your full text is preserved.'}
              </p>
              <div className="creation-concept-tools">
                <button type="button" className="btn btn--sm" onClick={() => dispatch({ type: 'setConcept', value: CONCEPT_EXAMPLE })}>Use full example</button>
                {state.concept && <button type="button" className="btn btn--sm" onClick={() => {
                  dispatch({ type: 'setConcept', value: '' });
                  conceptRef.current?.focus();
                }}>Clear concept</button>}
              </div>
              {state.pendingConcept !== null && (
                <div className="field__warning" role="alert" data-testid="concept-invalidation-warning">
                  Editing the concept invalidates your saved clarification answers and proposal provenance.
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    <button type="button" className="btn" onClick={() => dispatch({ type: 'confirmConceptEdit' })}>
                      Edit and invalidate answers
                    </button>
                    <button type="button" className="btn" onClick={() => dispatch({ type: 'cancelConceptEdit' })}>
                      Keep current concept
                    </button>
                  </div>
                </div>
              )}
              {showError('concept') && <div className="field__error">{errors.concept}</div>}
            </div>

            <div className="field creation-title-field">
              <label className="field__label" htmlFor="title">Title</label>
              <p className="muted" style={{ fontSize: 12, margin: '2px 0 6px' }}>Optional — leave it blank and Deej will propose one from your idea.</p>
              <input id="title" className="input" value={state.title}
                onChange={(e) => dispatch({ type: 'setTitle', value: e.target.value })}
                placeholder={proposedTitle} />
              {!state.title.trim() && state.concept.trim() && <p className="muted" data-testid="proposed-title">Proposed title: <strong>{proposedTitle}</strong></p>}
              {showError('title') && <div className="field__error">{errors.title}</div>}
              {showError('slug') && <div className="field__error">{errors.slug}</div>}
            </div>

            <details className="creation-idea-options" open={Boolean(showError('engine') || (state.submitAttempted && state.refUrls.some((_, i) => Boolean(errors[`refUrl${i}`]))))}>
              <summary>More options</summary>
              <details open={Boolean(showError('engine'))}>
                <summary>Builder</summary>
                <p>The default is selected for you. This chooses the builder, not the game’s rules or look.</p>
                <div className="field">
                  <label className="field__label" htmlFor="engine">Engine</label>
                  <select id="engine" className="select" value={state.engine?.model ?? ''}
                    onChange={(e) => dispatch({ type: 'setEngine', value: engineForId(e.target.value) ?? null })}>
                    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {showError('engine') && <div className="field__error">{errors.engine}</div>}
                </div>
              </details>
              <details open={state.submitAttempted && state.refUrls.some((_, i) => Boolean(errors[`refUrl${i}`]))}>
                <summary>Inspiration links</summary>
            <div className="field">
              <p className="field__label">Reference links (optional, up to {MAX_REFERENCE_URLS})</p>
              <p className="muted" style={{ fontSize: 12, margin: '2px 0 6px' }}>
                Pages describing the game or its inspiration (a wiki page, a rules page). Fetched
                once and summarized into the design brief.
              </p>
              {state.refUrls.map((url, i) => (
                <div key={i} style={{ marginBottom: 6 }}>
                  <input
                    id={`ref-url-${i}`}
                    aria-label={`Inspiration link ${i + 1}`}
                    className="input"
                    type="url"
                    placeholder="https://…"
                    value={url}
                    onChange={(e) => dispatch({ type: 'setRefUrl', index: i, value: e.target.value })}
                  />
                  {showError(`refUrl${i}`) && <div className="field__error">{errors[`refUrl${i}`]}</div>}
                </div>
              ))}
            </div>
              </details>
              <details>
              <summary>Players and controls</summary>
              <p>Already know how people should play? Choose here, or leave these unset and Deej picks what fits. Online players use separate devices; shared-device players use the same screen.</p>
              {(['playModes', 'orientation', 'inputScheme'] as const).map(id => <DecisionControl key={id}
                id={id} value={state.creatorAnswers[id]}
                onAnswer={(decision, value) => dispatch({ type: 'answer', decision, value })}
                error={showError(id)} />)}
              </details>
            </details>
          </>
        )}

        {serverError && <div className="error-text" role="alert">{serverError}</div>}
        {resumeLoading && serverError && (
          <button type="button" className="btn" disabled={!online} onClick={() => {
            setServerError('');
            setResumeAttempt((attempt) => attempt + 1);
          }}>Retry loading saved game</button>
        )}

        {state.step === 'concept' && <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          <button className="btn btn--primary" type="submit" disabled={busy || resumeLoading || !online} data-testid="wizard-submit">
            {busy ? 'Starting your game…' : 'Create my game'}
          </button>
        </div>}
      </form>
    </div>
  );
}
